import React, { useEffect } from "react";
import { DateRangeInput } from "@blueprintjs/datetime";
import moment from "moment-timezone";
import { AnchorButton, Spinner, Tooltip } from "@blueprintjs/core";
import { useAsync } from "react-use";
import { getIdToken, makeUrl } from "../../helpers/backendApi";

const DATE_FORMAT = "YYYY-MM-DD";
const MIN_DATE_STRING = "2024-04-04";
const MIN_DATE = moment(MIN_DATE_STRING).toDate();
const MAX_DATE = moment.utc().toDate();
const DEFAULT_START_DATE = moment.utc().subtract(7, "days").toDate();

export function TableDatePicker({
  selectedStartDate,
  selectedEndDate,
  setSelectedStartDate,
  setSelectedEndDate,
  downloadUrl,
  ...props
}) {
  useEffect(() => {
    const defaultStartDate = moment(DEFAULT_START_DATE).format(DATE_FORMAT);
    const defaultEndDate = moment(MAX_DATE).format(DATE_FORMAT);

    if (!selectedStartDate) {
      setSelectedStartDate(defaultStartDate);
    }

    if (!selectedEndDate) {
      setSelectedEndDate(defaultEndDate);
    }
  }, [
    selectedStartDate,
    selectedEndDate,
    setSelectedStartDate,
    setSelectedEndDate,
  ]);

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    if (start) {
      setSelectedStartDate(moment(start).format(DATE_FORMAT));
    }
    if (end) {
      setSelectedEndDate(moment(end).format(DATE_FORMAT));
    }
  };

  const areDatesValid = () => {
    if (!selectedStartDate || !selectedEndDate) return false;
    const start = moment(selectedStartDate);
    const end = moment(selectedEndDate);
    return start.isValid() && end.isValid() && start.isSameOrBefore(end);
  };

  return (
    <div style={{ display: "flex", gap: "10px" }}>
      <DateRangeInput
        value={[
          moment(selectedStartDate).toDate(),
          moment(selectedEndDate).toDate(),
        ]}
        onChange={handleDateRangeChange}
        popoverProps={{
          boundary: "viewport",
          usePortal: true,
        }}
        minDate={MIN_DATE}
        maxDate={MAX_DATE}
        formatDate={(date) => moment(date).format(DATE_FORMAT)}
        parseDate={(str) => moment(str, DATE_FORMAT).toDate()}
        closeOnSelection={true}
        canClearSelection={false}
        contiguousCalendarMonths={false}
        singleMonthOnly={false}
        shortcuts={false}
        allowSingleDayRange={true}
        showActionsBar={true}
        startInputProps={{ placeholder: "Start Date" }}
        endInputProps={{ placeholder: "End Date" }}
        {...props}
      />

      <DownloadActivityDataButton
        downloadUrl={downloadUrl}
        startDate={selectedStartDate}
        endDate={selectedEndDate}
        isDisabled={!areDatesValid()}
      />
    </div>
  );
}

function DownloadActivityDataButton({
  downloadUrl,
  startDate,
  endDate,
  isDisabled,
}) {
  const { value: xIdToken, loading } = useAsync(getIdToken, []);

  if (loading) {
    return <Spinner size={Spinner.SIZE_SMALL} />;
  }

  const href = makeUrl(downloadUrl, {
    startDate: encodeURIComponent(startDate),
    endDate: encodeURIComponent(endDate),
    xIdToken: encodeURIComponent(xIdToken),
  });

  return (
    <Tooltip content="Download Activity Data">
      <AnchorButton
        css={`
          color: white;
        `}
        minimal
        href={href}
        icon="download"
        target="_blank"
        disabled={isDisabled}
      />
    </Tooltip>
  );
}
